import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Briefcase, Flame, ArrowRight, ShieldCheck, Wallet, BarChart3, Users,
  Sparkles, Heart, Trophy, Download, Link2, Rocket, ArrowUpRight,
  Lock, BadgeCheck, Check, X, Zap, Clock, PiggyBank,
} from "lucide-react";
import { LANDING } from "@/constants/testIds";
import LandingFAQ from "@/pages/LandingFAQ";
import HomeLink from "@/components/HomeLink";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#0A0A0A] relative overflow-hidden">
      {/* Background spotlight */}
      <div className="absolute inset-0 spotlight-light pointer-events-none" />

      {/* Top navigation */}
      <nav className="relative z-20 max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
        <HomeLink testId="landing-logo" />
        <div className="flex items-center gap-3">
          <Link to="/como-funciona" className="hidden md:inline-flex" data-testid="landing-nav-how-it-works">
            <Button variant="ghost" className="text-[#0A0A0A] hover:bg-[#F5F5F1]">
              Como funciona
            </Button>
          </Link>
          <Link to="/login" data-testid={LANDING.loginLink}>
            <Button variant="ghost" className="text-[#0A0A0A] hover:bg-[#F5F5F1]">
              Entrar
            </Button>
          </Link>
          <Link to="/cadastro?role=creator">
            <Button className="bg-[#0A0A0A] hover:bg-[#1f1f1f] text-white hidden sm:inline-flex rounded-full px-5">
              Cadastro grátis
            </Button>
          </Link>
        </div>
      </nav>

      {/* Founding scarcity banner */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10" data-testid={LANDING.foundingBanner}>
        <div className="rounded-2xl bg-gradient-to-r from-[#FEE2E2] via-[#FED7AA] to-[#FEE2E2] border border-[#FCA5A5]/50 px-5 py-3 flex items-center gap-3 flex-wrap">
          <div className="w-2 h-2 rounded-full bg-[#DC2626] animate-pulse shrink-0" />
          <span className="text-sm font-semibold text-[#7F1D1D]">Beta em curso</span>
          <span className="text-sm text-[#7F1D1D]/80">
            Primeiras <strong>20 marcas fundadoras</strong> pagam <strong>0% de comissão nos primeiros 6 meses</strong>. Vagas limitadas.
          </span>
        </div>
      </div>

      {/* Hero (Opção A — direto) */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-12 pb-8">
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#E8E6E1] bg-white px-3 py-1.5 text-xs text-[#6B6B6B] mb-6 shadow-soft-red">
            <Flame className="w-3 h-3 text-[#DC2626]" />
            <span>Marketplace B2B de criadores e UGC no Brasil</span>
          </div>
          <h1 className="font-display font-bold text-5xl sm:text-6xl lg:text-[88px] tracking-[-0.04em] leading-[0.95] text-[#0A0A0A]">
            Contrate criadores<br />
            em <span className="warm-gradient bg-clip-text text-transparent">24 horas.</span><br />
            <span className="text-[#9B9B9B]">Pague só quando aprovar.</span>
          </h1>
          <p className="mt-8 text-lg lg:text-xl text-[#4A4A4A] max-w-2xl leading-relaxed">
            Sem mensalidade, sem burocracia. A plataforma que conecta sua marca aos criadores certos —
            com <strong className="text-[#0A0A0A]">pagamento protegido</strong> no cofre até você aprovar
            o resultado, e liberação via <strong className="text-[#0A0A0A]">PIX instantâneo</strong>.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <Link to="/cadastro?role=brand">
              <Button size="lg" className="bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-full px-7 py-6 text-base glow-red" data-testid={LANDING.registerBrand}>
                <Briefcase className="w-4 h-4 mr-2" /> Sou marca, quero contratar
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/cadastro?role=creator">
              <Button size="lg" variant="outline" className="bg-white border-[#E8E6E1] text-[#0A0A0A] hover:bg-[#F5F5F1] rounded-full px-7 py-6 text-base shadow-soft-red" data-testid={LANDING.registerCreator}>
                <Flame className="w-4 h-4 mr-2" /> Sou criador
              </Button>
            </Link>
          </div>

          {/* Trust badges below hero */}
          <div className="mt-8 flex items-center gap-6 flex-wrap text-xs text-[#6B6B6B]">
            <div className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-[#DC2626]" /> Pagamento Protegido</div>
            <div className="flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5 text-[#DC2626]" /> Selo digital verificável</div>
            <div className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-[#DC2626]" /> Saque PIX na hora</div>
            <div className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#DC2626]" /> LGPD</div>
          </div>
        </div>

        {/* Split CTAs - Two doors */}
        <div className="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Link to="/cadastro?role=brand" data-testid={LANDING.brandCta} className="group">
            <Card className="relative h-full overflow-hidden bg-white border border-[#E8E6E1] hover:border-[#DC2626]/40 transition-all duration-300 p-8 lg:p-10 shadow-soft-red hover:shadow-soft-red-lg">
              <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[#FEE2E2]/50 blur-3xl group-hover:bg-[#FEE2E2] transition-colors" />
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-[#F5F5F1] border border-[#E8E6E1] flex items-center justify-center mb-6">
                  <Briefcase className="w-5 h-5 text-[#DC2626]" />
                </div>
                <div className="text-xs uppercase tracking-[0.25em] text-[#DC2626] mb-3 font-semibold">Sou marca ou agência</div>
                <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight mb-3 text-[#0A0A0A]">
                  Encontre criadores<br />sob medida em minutos
                </h2>
                <p className="text-[#6B6B6B] mb-8 max-w-md">
                  Descubra criadores por nicho, engajamento e cidade. Contrate direto na plataforma
                  com pagamento protegido — libera só depois que você aprova.
                </p>
                <div className="flex items-center gap-3 text-[#DC2626] group-hover:gap-5 transition-all font-medium">
                  <span>Começar como marca</span>
                  <ArrowRight className="w-4 h-4" />
                </div>
                <Link
                  to="/como-funciona/match"
                  onClick={(e) => e.stopPropagation()}
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-[#DC2626] hover:text-[#B91C1C] transition-colors"
                  data-testid="landing-how-match-link"
                >
                  <Sparkles className="w-3.5 h-3.5" /> Como o Match Score escolhe o creator certo →
                </Link>
              </div>
            </Card>
          </Link>

          <Link to="/cadastro?role=creator" data-testid={LANDING.creatorCta} className="group">
            <Card className="relative h-full overflow-hidden surface-noir border-0 p-8 lg:p-10 grain shadow-soft-red-lg hover:shadow-soft-red-lg transition-all duration-300">
              <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[#DC2626]/20 blur-3xl group-hover:bg-[#DC2626]/40 transition-colors" />
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center mb-6">
                  <Sparkles className="w-5 h-5 text-[#FCA5A5]" />
                </div>
                <div className="text-xs uppercase tracking-[0.25em] text-[#FCA5A5] mb-3 font-semibold">Sou criador ou UGC</div>
                <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight mb-3 text-white">
                  Transforme<br />seu conteúdo em renda
                </h2>
                <p className="text-white/70 mb-8 max-w-md">
                  Receba campanhas alinhadas ao seu nicho. Defina seus preços, entregue no seu ritmo
                  e saque via PIX assim que a marca aprovar.
                </p>
                <div className="flex items-center gap-3 text-[#FCA5A5] group-hover:gap-5 transition-all font-medium">
                  <span>Começar como criador</span>
                  <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </Card>
          </Link>
        </div>
      </section>

      {/* Sprint P6.7 · Mini "Como funciona" section — funnel de descoberta */}
      <section
        className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-16 md:py-20 border-t border-[#E8E6E1]"
        data-testid="landing-how-it-works-section"
      >
        <div className="max-w-3xl mx-auto text-center mb-10 md:mb-12">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#E8E6E1] bg-white px-3 py-1.5 text-xs text-[#DC2626] mb-5 font-semibold uppercase tracking-wider">
            <Sparkles className="w-3 h-3" /> Como a Alta funciona
          </div>
          <h3 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-[-0.03em] leading-[1.05]">
            Do briefing ao <span className="warm-gradient bg-clip-text text-transparent">conteúdo aprovado</span> — em 4 passos
          </h3>
          <p className="text-[#6B6B6B] mt-5 text-base md:text-lg">
            A gente tira da sua frente tempo perdido, creator errado, calote, contrato confuso e imposto duplicado.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-4 mb-10">
          {[
            { n: "①", label: "Briefing", note: "~3 min", icon: Rocket, color: "#DC2626" },
            { n: "②", label: "Match Score", note: "6 fatores", icon: Sparkles, color: "#F59E0B" },
            { n: "③", label: "Contratação segura", note: "Cofre ativo", icon: Lock, color: "#10B981" },
            { n: "④", label: "Conteúdo aprovado", note: "Contrato + NF", icon: BadgeCheck, color: "#8B5CF6" },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.n} className="p-5 rounded-xl bg-white border border-[#E8E6E1] hover:border-[#DC2626]/30 hover:shadow-soft-red transition-all" data-testid={`landing-step-${s.n}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="font-display font-bold text-3xl" style={{ color: s.color }}>{s.n}</span>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${s.color}15` }}>
                    <Icon className="w-4 h-4" style={{ color: s.color }} />
                  </div>
                </div>
                <div className="text-xs uppercase tracking-wider text-[#9B9B9B] mb-1 font-semibold">{s.note}</div>
                <div className="font-display font-semibold text-base text-[#0A0A0A]">{s.label}</div>
              </div>
            );
          })}
        </div>

        <div className="text-center">
          <Link to="/como-funciona" data-testid="landing-hub-cta">
            <Button size="lg" className="bg-[#0A0A0A] hover:bg-[#1f1f1f] text-white rounded-full px-7">
              Entender o fluxo completo <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* NEW: Pagamento Protegido (Cofre) */}
      <section
        className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-24 border-t border-[#E8E6E1]"
        data-testid={LANDING.paymentSection}
      >
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#E8E6E1] bg-white px-3 py-1.5 text-xs text-[#DC2626] mb-5 font-semibold uppercase tracking-wider">
            <Lock className="w-3 h-3" /> Pagamento Protegido
          </div>
          <h3 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-[-0.03em] leading-[1]">
            Seu dinheiro fica seguro<br />
            até o trabalho estar <span className="warm-gradient bg-clip-text text-transparent">aprovado</span>
          </h3>
          <p className="text-[#6B6B6B] mt-6 text-lg">
            Quando a marca contrata, o valor vai direto para o cofre da Alta. O criador entrega, a marca aprova, o pagamento cai via PIX.
            <span className="block mt-2 text-[#9B9B9B] text-base">Funciona parecido com o pagamento retido do Airbnb ou do Mercado Livre — zero risco de calote pros dois lados.</span>
          </p>
        </div>

        {/* Timeline horizontal em 5 passos */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 lg:gap-4">
          {[
            { icon: Briefcase, label: "Marca contrata", sub: "Escolhe o criador e envia proposta", testId: LANDING.paymentStep1 },
            { icon: Lock, label: "Cofre retém", sub: "Valor bloqueado, não sai da plataforma", testId: LANDING.paymentStep2 },
            { icon: Sparkles, label: "Criador entrega", sub: "Envia o conteúdo pela plataforma", testId: LANDING.paymentStep3 },
            { icon: Check, label: "Marca aprova", sub: "Revisa e libera com 1 clique", testId: LANDING.paymentStep4 },
            { icon: Wallet, label: "PIX na hora", sub: "Criador saca instantaneamente", testId: LANDING.paymentStep5 },
          ].map((step, i, arr) => (
            <div key={i} className="relative" data-testid={step.testId}>
              <Card className="bg-white border border-[#E8E6E1] p-5 lg:p-6 h-full hover:border-[#DC2626]/30 hover:shadow-soft-red transition-all">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 rounded-xl warm-gradient flex items-center justify-center glow-red">
                    <step.icon className="w-4 h-4 text-white" />
                  </div>
                  <span className="font-mono text-xs text-[#D6D3CD] font-semibold">0{i + 1}</span>
                </div>
                <div className="font-display font-semibold text-base lg:text-lg mb-1 text-[#0A0A0A]">{step.label}</div>
                <p className="text-xs text-[#6B6B6B] leading-relaxed">{step.sub}</p>
              </Card>
              {/* Connector arrow (desktop only, except last) */}
              {i < arr.length - 1 && (
                <div className="hidden md:flex absolute top-1/2 -right-3 -translate-y-1/2 z-10">
                  <div className="w-6 h-6 rounded-full bg-white border border-[#E8E6E1] flex items-center justify-center shadow-soft-red">
                    <ArrowRight className="w-3 h-3 text-[#DC2626]" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* NEW: Selo Digital Verificável */}
      <section
        className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-24 border-t border-[#E8E6E1]"
        data-testid={LANDING.certificateSection}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#E8E6E1] bg-white px-3 py-1.5 text-xs text-[#DC2626] mb-5 font-semibold uppercase tracking-wider">
              <BadgeCheck className="w-3 h-3" /> Selo Digital
            </div>
            <h3 className="font-display font-bold text-4xl sm:text-5xl lg:text-[56px] tracking-[-0.03em] leading-[1.05] mb-6">
              Toda parceria com <span className="warm-gradient bg-clip-text text-transparent">certificado verificável</span>
            </h3>
            <p className="text-[#4A4A4A] text-lg mb-6 leading-relaxed">
              Cada contrato concluído gera um <strong>certificado digital assinado</strong> —
              a marca prova que investiu, o criador prova que entregou.
            </p>
            <ul className="space-y-3 mb-8">
              {[
                "Assinatura criptográfica SHA-256 única por contrato",
                "URL pública verificável (compartilhe no seu portfólio)",
                "PDF oficial pra apresentar contabilidade ou parceria futura",
                "Fim do 'print de WhatsApp' como prova de trabalho",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#FFF1F1] border border-[#FEE2E2] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-[#DC2626]" />
                  </div>
                  <span className="text-[#4A4A4A]">{item}</span>
                </li>
              ))}
            </ul>
            <div className="text-sm text-[#9B9B9B]">
              Nenhuma outra plataforma no Brasil oferece isso.
            </div>
          </div>

          {/* Certificado visual mockup */}
          <div className="relative">
            <div className="absolute -inset-4 warm-gradient rounded-3xl opacity-20 blur-2xl" />
            <Card className="relative bg-white border border-[#E8E6E1] p-8 lg:p-10 shadow-soft-red-lg">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl warm-gradient flex items-center justify-center">
                    <BadgeCheck className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#DC2626] font-semibold">Certificado de Parceria</div>
                    <div className="text-xs text-[#9B9B9B]">Alta Creators · 2026</div>
                  </div>
                </div>
                <div className="text-[10px] font-mono text-[#9B9B9B] bg-[#FAFAF7] border border-[#E8E6E1] rounded px-2 py-1">#A7F3D0</div>
              </div>

              <div className="border-t border-b border-dashed border-[#E8E6E1] py-5 my-5">
                <div className="text-xs text-[#9B9B9B] uppercase tracking-wider mb-1">Certificamos que</div>
                <div className="font-display font-bold text-xl text-[#0A0A0A] mb-4">@nome.do.criador</div>
                <div className="text-xs text-[#9B9B9B] uppercase tracking-wider mb-1">Realizou parceria com</div>
                <div className="font-display font-bold text-xl text-[#0A0A0A]">Marca Exemplo Ltda.</div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-xs mb-5">
                <div>
                  <div className="text-[#9B9B9B] uppercase tracking-wider mb-0.5">Entrega</div>
                  <div className="text-[#0A0A0A] font-medium">UGC · 3 vídeos</div>
                </div>
                <div>
                  <div className="text-[#9B9B9B] uppercase tracking-wider mb-0.5">Aprovado em</div>
                  <div className="text-[#0A0A0A] font-medium">10/02/2026</div>
                </div>
              </div>

              <div className="bg-[#FAFAF7] border border-[#E8E6E1] rounded-lg p-3 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#9B9B9B]">Assinatura SHA-256</div>
                  <div className="font-mono text-[10px] text-[#6B6B6B] mt-0.5 truncate">3f7d…c9a1</div>
                </div>
                <div className="flex items-center gap-1.5 text-[#DC2626] text-xs font-semibold">
                  <Check className="w-3 h-3" /> Verificado
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* "Por que se cadastrar" - 6 cards (KEEP com Comunidade reescrita) */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-24 border-t border-[#E8E6E1]">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#E8E6E1] bg-white px-3 py-1.5 text-xs text-[#DC2626] mb-5 font-semibold uppercase tracking-wider">
            Por que escolher a Alta Creators?
          </div>
          <h3 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-[-0.03em] leading-[1]">
            Tudo o que você precisa pra<br />
            <span className="warm-gradient bg-clip-text text-transparent">monetizar seu conteúdo</span>
          </h3>
          <p className="text-[#6B6B6B] mt-6 text-lg">
            Conectamos você às melhores marcas em campanhas alinhadas ao seu estilo.
            Combinação certeira, pagamento garantido. Simples assim.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { icon: Wallet, t: "PIX direto na conta", d: "Sem D+30 ou repasse quinzenal. Aprovou? Já pode sacar." },
            { icon: Sparkles, t: "Só campanhas que combinam", d: "Nada de spam. Mostramos só oportunidades alinhadas com seu nicho e engajamento real." },
            { icon: BarChart3, t: "Cresça sua audiência", d: "Trabalhe com marcas que impulsionam seu alcance orgânico ao vivo." },
            { icon: ShieldCheck, t: "Pagamento Protegido", d: "Valor fica no cofre até a marca aprovar. Proteção total pros dois lados." },
            { icon: BadgeCheck, t: "Selo digital verificável", d: "Cada contrato entrega um certificado assinado pra você provar sua parceria." },
            { icon: Trophy, t: "Reputação que abre portas", d: "Sistema de avaliações valoriza criadores que entregam qualidade — sua próxima campanha vem mais fácil." },
          ].map((s, i) => (
            <Card key={i} className="bg-white border border-[#E8E6E1] p-7 hover:border-[#DC2626]/30 hover:shadow-soft-red-lg transition-all duration-300 group">
              <div className="w-11 h-11 rounded-xl bg-[#FFF1F1] border border-[#FEE2E2] flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <s.icon className="w-5 h-5 text-[#DC2626]" />
              </div>
              <div className="font-display font-semibold text-lg mb-2 text-[#0A0A0A]">{s.t}</div>
              <p className="text-sm text-[#6B6B6B] leading-relaxed">{s.d}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* 3 passos simples (KEEP) */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-24 border-t border-[#E8E6E1]">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#E8E6E1] bg-white px-3 py-1.5 text-xs text-[#DC2626] mb-5 font-semibold uppercase tracking-wider">
            Como fazer parte?
          </div>
          <h3 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-[-0.03em] leading-[1]">
            Comece em <span className="warm-gradient bg-clip-text text-transparent">3 passos simples</span>
          </h3>
          <p className="text-[#6B6B6B] mt-6 text-lg">
            A partir de 1.000 seguidores você já pode se cadastrar.
            Aqui o que importa é seu engajamento e sua autenticidade.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { n: "01", icon: Download, t: "Crie sua conta", d: "Cadastro gratuito em menos de 2 minutos. Escolha se é marca ou criador." },
            { n: "02", icon: Link2, t: "Vincule suas redes", d: "Conecte Instagram, TikTok e mostre seus números reais — quanto mais completo, mais campanhas." },
            { n: "03", icon: Rocket, t: "Comece a ganhar", d: "Receba propostas no seu nicho. Entregue, aprove e saque via PIX em segundos." },
          ].map((s, i) => (
            <Card key={i} className="relative bg-white border border-[#E8E6E1] p-7 hover:border-[#DC2626]/30 hover:shadow-soft-red-lg transition-all">
              <div className="flex items-start justify-between mb-5">
                <div className="w-12 h-12 rounded-xl warm-gradient flex items-center justify-center glow-red">
                  <s.icon className="w-5 h-5 text-white" />
                </div>
                <span className="font-mono text-sm text-[#D6D3CD] font-semibold">{s.n}</span>
              </div>
              <div className="font-display font-semibold text-xl mb-2 text-[#0A0A0A]">{s.t}</div>
              <p className="text-sm text-[#6B6B6B] leading-relaxed">{s.d}</p>
            </Card>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link to="/cadastro?role=creator">
            <Button size="lg" className="bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-full px-8 py-6 text-base glow-red-lg">
              <Flame className="w-4 h-4 mr-2" /> Quero me cadastrar agora
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* NEW: Preço honesto (substitui stats falsos) */}
      <section
        className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-24 border-t border-[#E8E6E1]"
        data-testid={LANDING.pricingSection}
      >
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#E8E6E1] bg-white px-3 py-1.5 text-xs text-[#DC2626] mb-5 font-semibold uppercase tracking-wider">
            <PiggyBank className="w-3 h-3" /> Preço honesto
          </div>
          <h3 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-[-0.03em] leading-[1]">
            Você paga só quando<br />
            <span className="warm-gradient bg-clip-text text-transparent">gera negócio</span>
          </h3>
          <p className="text-[#6B6B6B] mt-6 text-lg">
            Sem letra miúda, sem contrato de fidelidade. Se a plataforma não gerar valor, você não paga nada.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
          <Card className="relative bg-white border border-[#E8E6E1] p-8 text-center shadow-soft-red hover:shadow-soft-red-lg transition-all" data-testid={LANDING.pricingTake}>
            <div className="text-xs uppercase tracking-[0.2em] text-[#9B9B9B] mb-3 font-semibold">Comissão Alta</div>
            <div className="font-display font-bold text-6xl lg:text-7xl tracking-[-0.03em] warm-gradient bg-clip-text text-transparent">15%</div>
            <div className="text-sm text-[#6B6B6B] mt-3">sobre cada campanha concluída</div>
            <div className="mt-4 inline-flex items-center gap-1.5 text-xs bg-[#FFF1F1] border border-[#FEE2E2] text-[#DC2626] rounded-full px-3 py-1 font-medium">
              <Sparkles className="w-3 h-3" /> 0% pros 20 primeiros fundadores
            </div>
          </Card>

          <Card className="relative bg-white border border-[#E8E6E1] p-8 text-center shadow-soft-red hover:shadow-soft-red-lg transition-all" data-testid={LANDING.pricingMonthly}>
            <div className="text-xs uppercase tracking-[0.2em] text-[#9B9B9B] mb-3 font-semibold">Mensalidade</div>
            <div className="font-display font-bold text-6xl lg:text-7xl tracking-[-0.03em] text-[#0A0A0A]">R$ 0</div>
            <div className="text-sm text-[#6B6B6B] mt-3">sem taxa de cadastro. sem letra miúda.</div>
            <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-[#6B6B6B]">
              <Check className="w-3 h-3 text-[#DC2626]" /> Cadastro grátis pra sempre
            </div>
          </Card>

          <Card className="relative bg-white border border-[#E8E6E1] p-8 text-center shadow-soft-red hover:shadow-soft-red-lg transition-all" data-testid={LANDING.pricingPix}>
            <div className="text-xs uppercase tracking-[0.2em] text-[#9B9B9B] mb-3 font-semibold">Saque</div>
            <div className="font-display font-bold text-6xl lg:text-7xl tracking-[-0.03em] warm-gradient bg-clip-text text-transparent">PIX</div>
            <div className="text-sm text-[#6B6B6B] mt-3">instantâneo pro criador</div>
            <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-[#6B6B6B]">
              <Zap className="w-3 h-3 text-[#DC2626]" /> Sem D+30, sem burocracia
            </div>
          </Card>
        </div>
      </section>

      {/* NEW: Comparação vs concorrentes */}
      <section
        className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-24 border-t border-[#E8E6E1]"
        data-testid={LANDING.comparisonSection}
      >
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#E8E6E1] bg-white px-3 py-1.5 text-xs text-[#DC2626] mb-5 font-semibold uppercase tracking-wider">
            <Trophy className="w-3 h-3" /> Comparação
          </div>
          <h3 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-[-0.03em] leading-[1]">
            O único marketplace do Brasil<br />
            com <span className="warm-gradient bg-clip-text text-transparent">cofre, selo e taxa transparente</span>
          </h3>
        </div>

        <Card className="bg-white border border-[#E8E6E1] p-6 lg:p-10 shadow-soft-red overflow-x-auto" data-testid={LANDING.comparisonTable}>
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="border-b border-[#E8E6E1]">
                <th className="text-left py-4 pr-4 font-semibold text-[#0A0A0A]">Recurso</th>
                <th className="text-center py-4 px-3 text-[#9B9B9B] font-medium">Agências tradicionais</th>
                <th className="text-center py-4 px-3 text-[#9B9B9B] font-medium">Outras plataformas</th>
                <th className="text-center py-4 pl-3 font-semibold">
                  <div className="inline-flex items-center gap-1.5 warm-gradient text-white px-3 py-1 rounded-full text-xs">
                    <Flame className="w-3 h-3" /> Alta Creators
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {[
                { r: "Mensalidade", a: "R$ 3-15 mil", b: "R$ 500-3 mil", c: "Zero" },
                { r: "Pagamento Protegido no cofre", a: "no", b: "no", c: "yes" },
                { r: "Selo digital verificável", a: "no", b: "no", c: "yes" },
                { r: "Comissão transparente", a: "sob consulta", b: "opaca", c: "15% fixa" },
                { r: "Saque PIX instantâneo", a: "no", b: "no", c: "yes" },
                { r: "Cadastro em minutos", a: "dias", b: "horas", c: "yes" },
                { r: "Sem fidelidade", a: "no", b: "às vezes", c: "yes" },
                { r: "App mobile PWA", a: "no", b: "às vezes", c: "yes" },
              ].map((row, i) => (
                <tr key={i} className="border-b border-[#F5F5F1] last:border-0">
                  <td className="py-3.5 pr-4 font-medium text-[#0A0A0A]">{row.r}</td>
                  <td className="text-center py-3.5 px-3 text-[#6B6B6B]">
                    {row.a === "no" ? <X className="w-4 h-4 text-[#9B9B9B] inline" /> :
                     row.a === "yes" ? <Check className="w-4 h-4 text-emerald-600 inline" /> : row.a}
                  </td>
                  <td className="text-center py-3.5 px-3 text-[#6B6B6B]">
                    {row.b === "no" ? <X className="w-4 h-4 text-[#9B9B9B] inline" /> :
                     row.b === "yes" ? <Check className="w-4 h-4 text-emerald-600 inline" /> : row.b}
                  </td>
                  <td className="text-center py-3.5 pl-3 font-semibold text-[#0A0A0A]">
                    {row.c === "yes" ? <Check className="w-5 h-5 text-[#DC2626] inline" /> :
                     row.c === "no" ? <X className="w-4 h-4 text-[#9B9B9B] inline" /> : row.c}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </section>

      {/* Vincule suas redes (banner dark - KEEP) */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <Card className="relative overflow-hidden surface-noir border-0 p-8 lg:p-14 grain shadow-soft-red-lg">
          <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-[#DC2626]/20 blur-3xl" />
          <div className="absolute -left-32 -bottom-32 w-96 h-96 rounded-full bg-[#F97316]/15 blur-3xl" />
          <div className="relative grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#FCA5A5] mb-3 font-semibold">Conecte-se</div>
              <h3 className="font-display font-bold text-3xl lg:text-5xl tracking-[-0.03em] mb-4 leading-[1.05] text-white">
                Vincule suas redes e <span className="bg-gradient-to-r from-[#FCA5A5] to-[#FB923C] bg-clip-text text-transparent">comece a ganhar</span>
              </h3>
              <p className="text-white/70 mb-6 leading-relaxed lg:text-lg">
                Quanto mais redes conectadas, mais oportunidades você recebe: campanhas compatíveis,
                análise real do seu alcance e recomendações personalizadas.
              </p>
              <Link to="/cadastro?role=creator">
                <Button className="bg-white text-[#0A0A0A] hover:bg-[#FCA5A5] hover:text-[#0A0A0A] rounded-full px-6 transition-colors">
                  <Link2 className="w-4 h-4 mr-2" /> Vincular minhas redes
                </Button>
              </Link>
            </div>
            <div className="space-y-3">
              {[
                "Campanhas compatíveis com seu perfil",
                "Análise real do seu alcance e engajamento",
                "Recomendações baseadas no seu nicho",
              ].map((b, i) => (
                <div key={i} className="flex items-center gap-3 bg-white/5 backdrop-blur border border-white/10 rounded-xl px-4 py-3.5">
                  <div className="w-6 h-6 rounded-full bg-[#DC2626]/30 border border-[#DC2626]/60 flex items-center justify-center flex-shrink-0">
                    <Heart className="w-3 h-3 text-[#FCA5A5]" />
                  </div>
                  <span className="text-sm text-white">{b}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </section>

      {/* Stats strip — reescrito com dados HONESTOS */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-12 border-t border-[#E8E6E1]">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { v: "15%", l: "Comissão Alta", d: "Única e transparente" },
            { v: "R$ 0", l: "Mensalidade", d: "Cadastro grátis pra sempre" },
            { v: "24h", l: "SLA de aprovação", d: "Contrato ativo em 1 dia" },
            { v: "PIX", l: "Saque na hora", d: "Zero D+30, zero espera" },
          ].map((k, i) => (
            <div key={i}>
              <div className="font-display font-bold text-4xl lg:text-5xl tracking-[-0.03em] warm-gradient bg-clip-text text-transparent">{k.v}</div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#9B9B9B] mt-2 font-medium">{k.l}</div>
              <div className="text-xs text-[#6B6B6B] mt-1">{k.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ — Perguntas frequentes */}
      <LandingFAQ />

      {/* Final CTA */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <Card className="relative overflow-hidden bg-white border border-[#E8E6E1] p-10 lg:p-16 text-center shadow-soft-red-lg">
          <div className="absolute inset-0 warm-gradient-soft opacity-50" />
          <div className="relative">
            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl warm-gradient flex items-center justify-center glow-red-lg">
              <Flame className="w-7 h-7 text-white" />
            </div>
            <h3 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-[-0.03em] mb-5 leading-[1]">
              Pronto pra <span className="warm-gradient bg-clip-text text-transparent">transformar</span><br />
              seu conteúdo em renda?
            </h3>
            <p className="text-[#6B6B6B] max-w-xl mx-auto mb-9 text-lg">
              Cadastro em 2 minutos. Zero taxa. Zero mensalidade.
              Seja um dos <strong>fundadores</strong> da nova geração de marketing de influência no Brasil.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/cadastro?role=creator">
                <Button size="lg" className="bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-full px-8 py-6 text-base glow-red-lg" data-testid={LANDING.registerCreator}>
                  <Flame className="w-4 h-4 mr-2" /> Sou criador
                </Button>
              </Link>
              <Link to="/cadastro?role=brand">
                <Button size="lg" variant="outline" className="bg-white border-[#E8E6E1] text-[#0A0A0A] hover:bg-[#F5F5F1] rounded-full px-8 py-6 text-base shadow-soft-red" data-testid={LANDING.registerBrand}>
                  <Briefcase className="w-4 h-4 mr-2" /> Sou marca, quero contratar
                </Button>
              </Link>
            </div>
          </div>
        </Card>
      </section>

      {/* Footer */}
      <footer className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-12 border-t border-[#E8E6E1] flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg warm-gradient flex items-center justify-center">
            <Flame className="w-4 h-4 text-white" />
          </div>
          <div className="text-sm text-[#6B6B6B]">
            <div className="font-display font-bold text-[#0A0A0A]">Alta Creators</div>
            <div className="text-xs text-[#9B9B9B]">© {new Date().getFullYear()} · Marketplace B2B de creators e UGC</div>
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs text-[#9B9B9B]">
          <span className="inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Pagamento Protegido</span>
          <span className="inline-flex items-center gap-1"><BadgeCheck className="w-3 h-3" /> Selo digital</span>
          <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> LGPD</span>
        </div>
        <div className="flex items-center gap-6 text-sm text-[#6B6B6B]">
          <Link to="/login" className="hover:text-[#0A0A0A]">Entrar</Link>
          <Link to="/cadastro?role=brand" className="hover:text-[#0A0A0A]">Sou marca</Link>
          <Link to="/cadastro?role=creator" className="hover:text-[#0A0A0A]">Sou criador</Link>
        </div>
      </footer>
    </div>
  );
}
